import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Search, Edit3, Check, X, ChevronDown, Eye,
  ImageOff, AlertCircle, Send,
} from "lucide-react";
import { LinkedOffersPanel } from "./Products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Same visual layout as ProductGomagTable — imagine · nume+SKU+stoc+brand+oferte
 *  · pret · performanță 30 zile · status. Feb 2026: user cerinta 100% paritate UI.
 */
export default function ProductsShopifyTab({ onOpenProduct }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bulkPushing, setBulkPushing] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedOffersId, setExpandedOffersId] = useState(null);
  const [editingPriceFor, setEditingPriceFor] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");
  const [savingPriceFor, setSavingPriceFor] = useState(null);
  const [pushingStockFor, setPushingStockFor] = useState(null);
  const [togglingStatusFor, setTogglingStatusFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shopify/products`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Nu am putut încărca produsele Shopify.", {
        description: e.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const syncFromShopify = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/shopify/products/sync`, {}, { withCredentials: true });
      toast.success(`Import Shopify: ${r.data.upserted} produse`);
      await load();
    } catch (e) {
      toast.error("Sync eșuat.", { description: e.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  };

  const bulkPushStock = async () => {
    if (!window.confirm(`Push stoc Oblio → Shopify pentru toate ${items.length} produsele?`)) return;
    setBulkPushing(true);
    try {
      const r = await axios.post(`${API}/shopify/products/push-oblio-stock`, {},
        { withCredentials: true });
      const { pushed = 0, skipped = 0, errors = 0 } = r.data || {};
      toast.success(`Bulk push stoc: ${pushed} trimise · ${skipped} deja OK · ${errors} eșec`);
    } catch (e) {
      toast.error("Bulk push eșuat.", { description: e.response?.data?.detail || e.message });
    }
    setBulkPushing(false);
    await load();
  };

  const startEditPrice = (it) => {
    setEditingPriceFor(it.sku);
    setPriceDraft(String(it.price ?? ""));
  };
  const savePrice = async (it) => {
    const val = parseFloat(priceDraft);
    if (!(val > 0)) { toast.error("Preț invalid"); return; }
    setSavingPriceFor(it.sku);
    try {
      const r = await axios.put(
        `${API}/shopify/products/${encodeURIComponent(it.sku)}/price`,
        { price: val }, { withCredentials: true },
      );
      toast.success(`Preț ${it.sku}: ${r.data.new_price} RON`);
      setItems((prev) => prev.map((x) => x.sku === it.sku ? { ...x, price: r.data.new_price, price_with_vat: r.data.new_price } : x));
      setEditingPriceFor(null);
    } catch (e) {
      toast.error("Push preț eșuat.", { description: e.response?.data?.detail || e.message });
    } finally { setSavingPriceFor(null); }
  };

  const pushStock = async (it) => {
    setPushingStockFor(it.sku);
    try {
      const r = await axios.post(
        `${API}/shopify/products/${encodeURIComponent(it.sku)}/push-stock`,
        {}, { withCredentials: true },
      );
      toast.success(`Stoc ${it.sku} → Shopify: ${r.data.stock_pushed}`);
      setItems((prev) => prev.map((x) => x.sku === it.sku
        ? { ...x, stock_shopify: r.data.stock_pushed } : x));
    } catch (e) {
      toast.error("Push stoc eșuat.", { description: e.response?.data?.detail || e.message });
    } finally { setPushingStockFor(null); }
  };

  const toggleStatus = async (it) => {
    const nextActive = it.status !== "active";
    const label = nextActive ? "activa" : "dezactiva";
    if (!window.confirm(`Sigur vrei să ${label} produsul "${it.gomag_name || it.title}" pe Shopify?`)) return;
    setTogglingStatusFor(it.sku);
    try {
      const r = await axios.post(
        `${API}/shopify/products/${encodeURIComponent(it.sku)}/toggle-status`,
        {}, { withCredentials: true },
      );
      const newStatus = r.data?.status || (nextActive ? "active" : "draft");
      // Shopify poate avea mai multe variante pentru acelasi produs -> actualizam toate randurile cu acelasi product_id
      setItems((prev) => prev.map((x) => (
        x.shopify_product_id && x.shopify_product_id === r.data?.shopify_product_id
          ? { ...x, status: newStatus }
          : x.sku === it.sku ? { ...x, status: newStatus } : x
      )));
      toast.success(`Status ${it.sku}: ${newStatus === "active" ? "Activat" : "Dezactivat (draft)"}`);
    } catch (e) {
      toast.error("Schimbare status eșuată.", { description: e.response?.data?.detail || e.message });
    } finally { setTogglingStatusFor(null); }
  };

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) =>
      (it.sku || "").toLowerCase().includes(q) ||
      (it.gomag_name || it.title || "").toLowerCase().includes(q) ||
      (it.emag_pnk || "").toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="space-y-4" data-testid="products-shopify-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută SKU / titlu / PNK..."
              className="w-72 rounded-md border border-zinc-800 bg-zinc-900 pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-emerald-500/40"
              data-testid="shopify-search"
            />
          </div>
          <span className="text-xs text-zinc-500">
            <b className="text-emerald-300">{filtered.length}</b> / {items.length} produse
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={bulkPushStock}
            disabled={bulkPushing || loading}
            data-testid="shopify-bulk-push-stock"
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-60"
          >
            {bulkPushing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {bulkPushing ? "Push în curs..." : "Push stoc Oblio → Shopify"}
          </button>
          <button
            onClick={syncFromShopify}
            disabled={syncing}
            data-testid="shopify-sync-btn"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-emerald-500/40 disabled:opacity-60"
          >
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {syncing ? "Sync..." : "Sincronizează cu Shopify"}
          </button>
        </div>
      </div>

      {/* Table — layout identic cu ProductGomagTable */}
      <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/70">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="shopify-products-table">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-3 w-10"><input type="checkbox" className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900" /></th>
                <th className="px-3 py-3 w-24">Imagine</th>
                <th className="px-3 py-3">Nume</th>
                <th className="px-3 py-3 text-right">Preț fără taxă</th>
                <th className="px-3 py-3 text-center">Performanță (30 zile)</th>
                <th className="px-3 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/80">
              {loading && <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500">Se încarcă...</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">
                  {items.length === 0
                    ? <>Niciun produs. Apasă <span className="text-emerald-300">Sincronizează cu Shopify</span>.</>
                    : <>Niciun rezultat pentru căutare.</>}
                </td></tr>
              )}
              {!loading && filtered.map((it) => {
                const active = it.status === "active";
                const isExpanded = expandedOffersId === it.sku;
                const priceNoVat = it.price_no_vat;
                const priceWithVat = it.price_with_vat ?? it.price;
                const showBoth = priceNoVat && priceWithVat && Math.abs(priceWithVat - priceNoVat) > 0.01;
                const canOpenModal = !!(it.gomag_product_id && onOpenProduct);
                const outOfSyncStock = (it.stock_oblio || 0) !== (it.stock_shopify || 0);
                return (
                  <React.Fragment key={it.sku}>
                    <tr
                      data-testid={`shopify-row-${it.sku}`}
                      onClick={() => canOpenModal && onOpenProduct(it.gomag_product_id)}
                      className={`text-zinc-200 hover:bg-zinc-900/40 ${canOpenModal ? "cursor-pointer" : ""}`}
                    >
                      <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900" />
                      </td>
                      <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <div className="h-16 w-16 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/60">
                          {it.gomag_image_url ? (
                            <img src={it.gomag_image_url} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-zinc-600">
                              <ImageOff className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="text-sm font-medium leading-snug text-zinc-100 break-words" title={it.gomag_name || it.title}>
                          {it.gomag_name || it.title}
                        </p>
                        <div className="mt-2 space-y-0.5 text-xs">
                          <p className="text-zinc-500">SKU: <span className="font-mono font-semibold text-zinc-300">{it.sku}</span></p>
                          <p className="text-zinc-500">
                            Stoc Oblio: <span className={(it.stock_oblio || 0) > 0 ? "font-bold text-zinc-100" : "font-bold text-rose-400"}>{it.stock_oblio || 0}</span>
                            {" · "}
                            <span className="text-zinc-500">Shopify: </span>
                            <span className={outOfSyncStock ? "font-bold text-amber-300" : "font-bold text-zinc-400"}>{it.stock_shopify || 0}</span>
                          </p>
                          {it.gomag_brand && <p className="text-zinc-500">Brand: <span className="text-zinc-300">{it.gomag_brand}</span></p>}
                        </div>
                        <div className="mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {(it.gomag_url || it.public_url) && (
                            <a
                              href={it.gomag_url || it.public_url}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`shopify-link-site-${it.sku}`}
                              title="Vezi pe bragus.ro"
                              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[10px] font-medium text-sky-200 hover:bg-sky-500/20"
                            >
                              <Eye className="h-3 w-3" /> Vezi pe site
                            </a>
                          )}
                          {it.gomag_product_id && (
                            <button
                              data-testid={`shopify-toggle-offers-${it.sku}`}
                              onClick={() => setExpandedOffersId(isExpanded ? null : it.sku)}
                              className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] font-medium text-zinc-300 hover:border-orange-500/40 hover:text-orange-200"
                            >
                              Oferte
                              {it.linked_offers_count > 0 && (
                                <span
                                  data-testid={`shopify-linked-offers-badge-${it.sku}`}
                                  title={`${it.linked_offers_count} oferte marketplace linkuite`}
                                  className="inline-flex items-center justify-center rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold leading-none text-emerald-200"
                                >
                                  {it.linked_offers_count}
                                </span>
                              )}
                              <ChevronDown className={`h-3 w-3 transition ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                          )}
                          {outOfSyncStock && (
                            <button
                              onClick={() => pushStock(it)}
                              disabled={pushingStockFor === it.sku}
                              data-testid={`shopify-push-stock-${it.sku}`}
                              title={`Push ${it.stock_oblio || 0} → Shopify`}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                            >
                              {pushingStockFor === it.sku ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                              Push stoc
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right align-top" onClick={(e) => e.stopPropagation()}>
                        {editingPriceFor === it.sku ? (
                          <div className="inline-flex items-center gap-1">
                            <input
                              value={priceDraft}
                              onChange={(e) => setPriceDraft(e.target.value)}
                              className="w-20 rounded bg-zinc-900 border border-emerald-500/50 px-1.5 py-0.5 text-right font-mono text-zinc-100 text-xs"
                              autoFocus
                              data-testid={`shopify-price-input-${it.sku}`}
                            />
                            <button onClick={() => savePrice(it)} disabled={savingPriceFor === it.sku}
                              className="rounded p-1 text-emerald-300 hover:bg-emerald-500/20">
                              {savingPriceFor === it.sku ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            </button>
                            <button onClick={() => setEditingPriceFor(null)}
                              className="rounded p-1 text-red-300 hover:bg-red-500/20">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="inline-flex flex-col items-end">
                            <button
                              onClick={() => startEditPrice(it)}
                              data-testid={`shopify-price-btn-${it.sku}`}
                              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono font-semibold text-zinc-100 hover:bg-zinc-800"
                            >
                              {Number(priceNoVat || priceWithVat || 0).toFixed(2)}
                              <span className="text-[10px] font-normal text-zinc-500">RON</span>
                              <Edit3 className="h-2.5 w-2.5 text-zinc-500" />
                            </button>
                            {showBoth && (
                              <p className="text-[10px] text-zinc-500 mt-0.5">
                                {Number(priceWithVat).toFixed(2)} RON cu TVA
                              </p>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center align-top">
                        {(it.units_sold_30d || 0) > 0 ? (
                          <div>
                            <p className="text-sm font-semibold text-emerald-300">{Number(it.sales_last_30d || 0).toFixed(2)} <span className="text-[10px]">RON</span></p>
                            <p className="text-[11px] text-zinc-500">{it.units_sold_30d} buc vândute</p>
                          </div>
                        ) : (
                          <p className="text-[11px] leading-snug text-zinc-500">Nici o vânzare<br />în ultimele 30 zile</p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center align-top" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-center gap-1" data-testid={`shopify-status-${it.sku}`}>
                          <span className={`text-[11px] font-medium ${active ? "text-emerald-300" : "text-zinc-500"}`}>{active ? "Activat" : "Inactiv"}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleStatus(it); }}
                            disabled={togglingStatusFor === it.sku}
                            data-testid={`shopify-status-toggle-${it.sku}`}
                            title={active ? "Click pentru a dezactiva (draft) pe Shopify" : "Click pentru a activa pe Shopify"}
                            aria-pressed={active}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:opacity-50 ${active ? "bg-emerald-500 hover:bg-emerald-400" : "bg-zinc-700 hover:bg-zinc-600"}`}
                          >
                            {togglingStatusFor === it.sku ? (
                              <Loader2 className="mx-auto h-3 w-3 animate-spin text-white" />
                            ) : (
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${active ? "translate-x-4" : "translate-x-0.5"}`} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && it.gomag_product_id && (
                      <tr data-testid={`shopify-offers-expanded-${it.sku}`} className="bg-zinc-900/30">
                        <td colSpan={6} className="px-6 py-3">
                          <LinkedOffersPanel productId={it.gomag_product_id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
